/**
 * Panel Turnos - Real-time queue display component
 */

import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Clock, Users, AlertCircle } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";

import type { TurnoListItem } from "@/hooks/use-turnos";

interface PanelTurnosProps {
  turnos: TurnoListItem[];
  turnoActual?: TurnoListItem | null;
  isLoading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  onSelectTurno?: (turno: TurnoListItem) => void;
}

export function PanelTurnos({
  turnos,
  turnoActual,
  isLoading = false,
  error = null,
  onSelectTurno,
}: PanelTurnosProps) {
  const { t } = useTranslation();

  const getPrioridadBadge = (prioridad: string) => {
    switch (prioridad) {
      case "ALTA":
        return <Badge variant="destructive">{t("turnos.priority.high", "Alta")}</Badge>;
      case "MEDIA":
        return (
          <Badge variant="default" className="bg-yellow-500">
            {t("turnos.priority.medium", "Media")}
          </Badge>
        );
      default:
        return <Badge variant="secondary">{t("turnos.priority.low", "Baja")}</Badge>;
    }
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    return `${hours}:${minutes}`;
  };

  // Filter waiting turns
  const turnosEsperando = turnos.filter((t) => t.estado === "ESPERANDO");

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center text-destructive">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p>{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Current Turn Display */}
      <Card className="bg-primary text-primary-foreground">
        <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
          <CardTitle className="text-base sm:text-lg">
            {t("turnos.currentTurno", "Turno Actual")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
          {isLoading ? (
            <Skeleton className="h-20 sm:h-24 w-full bg-primary-foreground/20" />
          ) : turnoActual ? (
            <div className="text-center py-2 sm:py-4">
              <p className="text-6xl sm:text-8xl font-bold animate-pulse">
                {String(turnoActual.numero).padStart(3, "0")}
              </p>
              <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
                {turnoActual.punto_atencion_nombre && (
                  <Badge
                    variant="outline"
                    className="border-primary-foreground text-primary-foreground text-xs sm:text-sm"
                  >
                    {turnoActual.punto_atencion_nombre}
                  </Badge>
                )}
                {getPrioridadBadge(turnoActual.prioridad)}
                {turnoActual.tipo_servicio && (
                  <Badge
                    variant="outline"
                    className="border-primary-foreground text-primary-foreground text-xs sm:text-sm capitalize"
                  >
                    {turnoActual.tipo_servicio}
                  </Badge>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8 text-primary-foreground/70">
              <p className="text-sm sm:text-lg">
                {t("turnos.noCurrentTurno", "Sin turno en atención")}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Waiting Queue */}
      <Card>
        <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Users className="h-4 w-4 sm:h-5 sm:w-5" />
              {t("turnos.waitingQueue", "Cola de Espera")}
            </CardTitle>
            <Badge variant="outline" className="text-xs sm:text-sm">
              {turnosEsperando.length} {t("turnos.waiting", "esperando")}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 sm:h-16 w-full" />
              ))}
            </div>
          ) : turnosEsperando.length === 0 ? (
            <div className="text-center py-6 sm:py-8 text-muted-foreground">
              <Users className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm sm:text-base">
                {t("turnos.noWaiting", "No hay turnos en espera")}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[250px] sm:h-[400px]">
              <div className="space-y-2">
                {turnosEsperando.map((turno, index) => (
                  <div
                    key={turno.id}
                    onClick={() => onSelectTurno?.(turno)}
                    className={`flex items-center justify-between p-2 sm:p-3 rounded-lg border transition-colors ${
                      index === 0 ? "bg-accent border-primary" : "bg-muted/50"
                    } ${onSelectTurno ? "cursor-pointer hover:bg-accent/80 hover:border-primary" : ""}`}
                  >
                    <div className="flex items-center gap-2 sm:gap-3">
                      <span className="text-xl sm:text-2xl font-bold w-12 sm:w-16">
                        {String(turno.numero).padStart(3, "0")}
                      </span>
                      <div>
                        <div className="flex items-center gap-1 flex-wrap">
                          {getPrioridadBadge(turno.prioridad)}
                          {turno.tipo_servicio && (
                            <Badge variant="outline" className="text-xs capitalize">
                              {turno.tipo_servicio}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <Clock className="h-3 w-3" />
                          {formatTime(turno.hora_llegada)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-1 sm:gap-2">
                      {turno.tiempo_espera !== null && (
                        <span className="text-xs sm:text-sm text-muted-foreground hidden xs:inline">
                          {turno.tiempo_espera} {t("turnos.minutes", "min")}
                        </span>
                      )}
                      {index === 0 && (
                        <Badge variant="default" className="text-xs">
                          {t("turnos.next", "Siguiente")}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Date/Time Display */}
      <Card>
        <CardContent className="py-3 sm:py-4 px-3 sm:px-6">
          <div className="flex items-center justify-center gap-2 sm:gap-4 text-muted-foreground">
            <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="text-sm sm:text-lg">
              {format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy - HH:mm", {
                locale: es,
              })}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
